/**
 * urls — canonical builders for every course-shell URL (TASK-014). Single
 * source of truth for the path shapes declared in config/routes.rb so
 * CourseTabs / Overview / Lettura / Padronanza / QuizHub / QuizSession /
 * Tutor never hand-roll a literal template string more than once.
 */

/** `/read/:id` — Panoramica (landing). */
export function overviewUrl(versionId: number): string {
  return `/read/${versionId}`
}

/**
 * `/read/:id/lettura` — bare entry point (no slug). Server-side redirect
 * (ReaderController#lettura_entry) to the user's reading position, same
 * resolution ReaderController#show used to do before it became #overview.
 * This is the URL the "Manuale" tab links to — CourseTabs never knows the
 * current slug (shell carries no reading-position data by contract).
 */
export function letturaEntryUrl(versionId: number): string {
  return `/read/${versionId}/lettura`
}

/** `/read/:id/lettura/:slug` — a specific reading section. */
export function sectionUrl(versionId: number, slug: string): string {
  return `/read/${versionId}/lettura/${slug}`
}

/** `/read/:id/quiz` — quiz hub (sessions list + quick start). */
export function quizHubUrl(versionId: number): string {
  return `/read/${versionId}/quiz`
}

/** `/read/:id/quiz/:sessionId` — a single quiz session (focus or results). */
export function quizSessionUrl(versionId: number, sessionId: string): string {
  return `/read/${versionId}/quiz/${sessionId}`
}

/** `/read/:id/tutor` — conversations list. */
export function tutorUrl(versionId: number): string {
  return `/read/${versionId}/tutor`
}

/**
 * `/read/:id/tutor/:conversationId` — a single tutor conversation.
 *
 * `draft` (TASK-014 Step B2, "primo messaggio da Panoramica" handoff):
 * appended as `?draft=<urlencoded text>` when the caller wants
 * `pages/reader/Tutor.tsx` to auto-send that text as the conversation's
 * first message on mount (see that page's `TutorConversationPane`). Omitted
 * (no `?draft=`) for a plain navigation to an existing conversation.
 */
export function tutorConversationUrl(
  versionId: number,
  conversationId: string,
  draft?: string,
): string {
  const base = `/read/${versionId}/tutor/${conversationId}`
  return draft != null && draft.length > 0 ? `${base}?draft=${encodeURIComponent(draft)}` : base
}

/** `/read/:id/padronanza` — mastery report. */
export function padronanzaUrl(versionId: number): string {
  return `/read/${versionId}/padronanza`
}

/** `/read/:id/mappa` — concept map (TASK-016 Phase 3). */
export function mappaUrl(versionId: number): string {
  return `/read/${versionId}/mappa`
}

/**
 * `/read/:id/pdf` — PDF download (TASK-014 Part 2). A plain browser
 * navigation: ReaderController#pdf redirects to the attached blob with
 * `disposition: attachment`, so a bare `<a href={pdfUrl(...)}>` triggers the
 * download — no client-side fetch needed. Only rendered when
 * `shell.pdfAvailable` (see CourseLayout.tsx).
 */
export function pdfUrl(versionId: number): string {
  return `/read/${versionId}/pdf`
}
